import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppComponent } from './app.component';
import { MacUserService } from './services/user/mac-user.service';
import { HttpClient, HttpRequest, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { environment } from '../environments/environment';
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt';
import { CommonModule, DATE_PIPE_DEFAULT_TIMEZONE, registerLocaleData } from '@angular/common';
import { ServiceWorkerModule } from '@angular/service-worker';
import { LoginComponent } from './routes/user/login/login.component';
import { ForgotPasswordComponent } from './routes/user/forgot-password/forgot-password.component';
import { PasswordResetComponent } from './routes/user/password-reset/password-reset.component';
import { FormsModule } from '@angular/forms';
import { ReloadPageComponent } from './routes/reload-page/reload-page.component';
import { DashboardComponent } from './routes/dashboard/dashboard.component';
import { DBConfig, NgxIndexedDBModule } from 'ngx-indexed-db';

import { SharedModule } from './modules/_shared/shared.module';
import { SupportModule } from './modules/support/support.module';

import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';
import Bugsnag from '@bugsnag/js';
import packageInfo from '../../package.json';
import { NgBootstrapDarkmodeModule } from 'ng-bootstrap-darkmode';
import localeEnAu from '@angular/common/locales/en-AU';
import { ApiServiceInterface, provideNgxh6, SYNC_CONFIG } from '@hutsix/ngxh6';
import { custom_modals } from './config/modals-config';
import { role_hierarchy } from './config/users-config';
import { custom_filters, custom_forms, getFormsConfig } from './config/forms-config';
import { NavbarComponent } from './components/navbar/navbar.component';
import { MacFormService } from './services/form/form.service';
import { syncConfigFactory } from './config/sync-config';
import { getCrudsConfig } from './config/cruds-config';
import moment from 'moment-timezone';
import { getReportsConfig } from './config/reports-config';

registerLocaleData(localeEnAu);

Bugsnag.start({
    apiKey: 'b64f6ba85e9c9662c02eb1d533e13a4a',
    releaseStage: environment.env,
    appVersion: packageInfo.version + (environment.env === 'staging' ? '-staging' : ''),
});

export function errorHandlerFactory(): BugsnagErrorHandler {
    return new BugsnagErrorHandler();
}

const dbConfig: DBConfig = {
    name: 'appDb',
    version: 1,
    objectStoresMeta: [
        {
            store: 'offlineCache',
            storeConfig: { keyPath: 'ulid', autoIncrement: false },
            storeSchema: [
                { name: 'idx', keypath: ['endpoint', 'user'], options: { unique: false } },
                { name: 'endpoint', keypath: 'endpoint', options: { unique: false } },
                { name: 'user', keypath: 'user', options: { unique: false } },
                { name: 'ulid', keypath: 'ulid', options: { unique: true } },
                { name: 'data', keypath: 'data', options: { unique: false } },
            ],
        },
        {
            store: 'apiCache',
            storeConfig: { keyPath: ['endpoint', 'user'], autoIncrement: false },
            storeSchema: [
                { name: 'idx', keypath: ['endpoint', 'user'], options: { unique: true } },
                { name: 'endpoint', keypath: 'endpoint', options: { unique: false } },
                { name: 'user', keypath: 'user', options: { unique: false } },
                { name: 'timestamp', keypath: 'timestamp', options: { unique: false } },
                { name: 'data', keypath: 'data', options: { unique: false } },
            ],
        },
    ],
};

export function jwtOptionsFactory(userService: MacUserService): object {
    return {
        tokenGetter: (r: HttpRequest<any>) => {
            if (r && r.headers.has('Authorization') && r.headers.get('Authorization')) {
                return r.headers.get('Authorization');
            }
            return userService.token;
        },
        allowedDomains: environment.whitelistedDomains,
        disallowedRoutes: environment.blacklistedRoutes,
    };
}

@NgModule({
    declarations: [AppComponent, LoginComponent, ForgotPasswordComponent, PasswordResetComponent, ReloadPageComponent, DashboardComponent, NavbarComponent],
    bootstrap: [AppComponent],
    imports: [
        CommonModule,
        FormsModule,
        BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        SharedModule,
        SupportModule,
        NgBootstrapDarkmodeModule,
        JwtModule.forRoot({
            jwtOptionsProvider: {
                provide: JWT_OPTIONS,
                useFactory: jwtOptionsFactory,
                deps: ['UserService'],
            },
        }),
        ServiceWorkerModule.register('custom-service-worker.js', { enabled: environment.production }),
        NgxIndexedDBModule.forRoot(dbConfig),
    ],
    providers: [
        { provide: ErrorHandler, useFactory: errorHandlerFactory },
        { provide: LOCALE_ID, useValue: 'en-AU' },
        { provide: DATE_PIPE_DEFAULT_TIMEZONE, useValue: '+0930' },
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
        provideNgxh6({
            config: {
                reportConfig: {
                    reports: getReportsConfig(),
                },
                crudConfig: {
                    cruds: getCrudsConfig(),
                },
                formConfig: {
                    forms: getFormsConfig(),
                    customForms: custom_forms,
                    customFilters: custom_filters,
                },
                userConfig: {
                    roleHierarchy: role_hierarchy,
                },
                apiConfig: {
                    apiUrl: environment.apiUrl,
                    imgixDomain: environment.imgixDomain,
                },
                modalConfig: custom_modals,
                syncConfigProvider: {
                    provide: SYNC_CONFIG,
                    useFactory: syncConfigFactory,
                    deps: ['UserService', 'ApiService', HttpClient],
                },
            },
            userService: MacUserService,
            formService: MacFormService,
        }),
    ],
})
export class AppModule {
    constructor() {
        moment.tz.setDefault('Australia/Darwin');
    }
}
